// Normalizer between G1's internal gameKey variants and Hydra's canonical
// analytics gameKey. Any panel wired to /api/analytics/* that takes a gameKey
// param (trades, candles, cross-market-spread, ticks) must pass through here.
//
// Hydra canonical:  "YYYY-MM-DD|TEAM_A|TEAM_B"  (teams alpha-sorted, no sport)
// G1 variants seen in code:
//   - doubleheader-safe v2 key "${sport}|${date}|${gameNo}|${TEAM_LO}|${TEAM_HI}"
//     (marketStore row keys — the uniform 5-seg grouping key)
//   - canonical pass-through from Hydra (marketStore exposes `canonicalGameKey`)
//   - "${sport}|${date}|${awayCode}|${homeCode}"  (marketStore fallback when no canonical)
//   - "${date}|${away}|${home}"                   (date-first variant, order preserved)
//
// Hydra strips `sport` and alpha-sorts the teams so cross-exchange alias
// mismatches (e.g. Kalshi CHW vs Polymarket CWS) collapse into one key.
// See hydra/BEHAVIORS.md "Cross-exchange game matching".

import { PROP_SYNTH_MARKER } from '@/lib/sport/propSynthKey';

import { SPORT_NORMALIZE } from '../stores/marketFrameNormalization';

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

/** Fold a sport spelling to g1's sport id for key<->URL comparison. Canonical
 *  v2 keys carry HYDRA vocabulary (`ncaaf` for college football); URLs and
 *  marketStore rows carry g1 ids (`cfb`). Comparing raw strings 404'd every
 *  CFB game-detail link the day he2's core-owned canonical keys landed.
 *  Unknown spellings fall back to the raw string (never widen a mismatch). */
function foldKeySport(s: string): string {
  return SPORT_NORMALIZE[s] ?? SPORT_NORMALIZE[s.toUpperCase()] ?? s;
}

export interface HydraGameKeyParts {
  date: string;
  teamA: string;
  teamB: string;
}

/**
 * Convert any G1 gameKey variant to Hydra canonical. Returns null if the
 * input doesn't match a known shape so callers can drop malformed keys
 * rather than send garbage upstream.
 */
export function toHydraGameKey(key: string | null | undefined): string | null {
  if (!key) return null;
  const parts = key.split('|');

  let date: string;
  let a: string;
  let b: string;

  if (parts.length === 3) {
    // date|away|home
    [date, a, b] = parts as [string, string, string];
  } else if (parts.length === 4) {
    // sport|date|away|home — drop sport
    [, date, a, b] = parts as [string, string, string, string];
  } else if (parts.length === 5) {
    // sport|date|gameNo|away|home (doubleheader-safe v2) — drop sport + gameNo.
    // The analytics surface keys games by date+teams only (no gameNo), so a
    // doubleheader collapses to one analytics key — acceptable for the
    // trades/candles history surface, which is out of the live-feed scope.
    [, date, , a, b] = parts as [string, string, string, string, string];
  } else {
    return null;
  }

  if (!DATE_RE.test(date)) return null;
  if (!a || !b) return null;

  const [teamA, teamB] = [a, b].sort();
  return `${date}|${teamA}|${teamB}`;
}

/**
 * Convert a g1 gameKey to the key used for the LIVE game-states poll
 * (`/api/analytics/game-states`) and its `gameStatesStore` lookups.
 *
 * Unlike `toHydraGameKey` (the trades/candles HISTORY normalization, which
 * deliberately collapses a doubleheader to one date+teams key), this PRESERVES
 * the full 5-seg v2 identity: hydra's GameStateCache is keyed by the v2 key,
 * and its legacy-key bridge resolves an undisambiguated key to "the live game
 * first" — which put game 1's live score on BOTH rows of the 2026-07-11
 * MIL@PIT doubleheader. Sending the v2 key addresses one specific game.
 *
 * Writers (useGameStatesPoll key lists) and readers (gameStatesStore lookups)
 * must both use this helper — the store is keyed by the requested string.
 * Sport-less (3-seg) inputs fall back to the collapsed key, which the hydra
 * bridge still resolves.
 */
export function toHydraGameKeyLive(key: string | null | undefined): string | null {
  const parsed = parseGameKeyV2(key);
  if (!parsed) return null;
  if (!parsed.sport) return `${parsed.date}|${parsed.teamLo}|${parsed.teamHi}`;
  return `${parsed.sport}|${parsed.date}|${parsed.gameNo}|${parsed.teamLo}|${parsed.teamHi}`;
}

// ── Doubleheader-safe v2 game key ──────────────────────────────────
//
// Shape: "{sport}|{YYYY-MM-DD}|{gameNo}|{TEAM_LO}|{TEAM_HI}" — lowercase sport,
// alpha-sorted teams, integer gameNo (1 for a normal single game, >1 only for a
// doubleheader's later games). Hydra emits this as `canonicalGameKeyV2` on every
// market + consensus frame, alongside the unchanged legacy keys. g1 uses it as
// the row identity / grouping key, the per-game route param, and the odds/chart
// stream route param. The hydra stream routes accept the v2 key, the legacy
// 4-seg, OR the legacy 3-seg key — sending the v2 key addresses a specific
// doubleheader game; an undisambiguated key resolves to the live game.

export interface GameKeyV2Parts {
  /** Lowercase sport id, or null when the source key omitted it (3-seg analytics key). */
  sport: string | null;
  date: string;
  /** 1 for a normal single game; >1 for a doubleheader's later games. */
  gameNo: number;
  /** Alpha-first / alpha-second uppercase team codes (order-independent). */
  teamLo: string;
  teamHi: string;
}

/**
 * Parse any g1/hydra game-key variant into doubleheader-aware parts:
 *   5-seg  sport|date|gameNo|A|B   (canonicalGameKeyV2)
 *   4-seg  sport|date|A|B          (legacy canonicalGameKey)       → gameNo 1
 *   3-seg  date|A|B                (analytics key, sport-less)     → gameNo 1, sport null
 * Teams are alpha-sorted + uppercased so the result is order-independent.
 * Returns null for non-canonical strings (event tickers, malformed input).
 */
export function parseGameKeyV2(key: string | null | undefined): GameKeyV2Parts | null {
  if (!key) return null;
  const parts = key.split('|');
  let sport: string | null;
  let date: string;
  let gameNo = 1;
  let a: string;
  let b: string;
  if (parts.length === 5) {
    const [s, d, g, x, y] = parts;
    sport = s ? s.toLowerCase() : null;
    date = d;
    const n = Number(g);
    if (!Number.isInteger(n) || n < 1) return null;
    gameNo = n;
    a = x; b = y;
  } else if (parts.length === 4) {
    const [s, d, x, y] = parts;
    sport = s ? s.toLowerCase() : null;
    date = d; a = x; b = y;
  } else if (parts.length === 3) {
    const [d, x, y] = parts;
    sport = null; date = d; a = x; b = y;
  } else {
    return null;
  }
  if (!DATE_RE.test(date)) return null;
  if (!a || !b) return null;
  const [teamLo, teamHi] = [a.toUpperCase(), b.toUpperCase()].sort();
  return { sport, date, gameNo, teamLo, teamHi };
}

/**
 * Promote a legacy canonicalGameKey (or a marketStore parsed fallback) to the
 * uniform 5-seg v2 grouping shape with gameNo=1. A normal (single) game keys
 * IDENTICALLY whether or not hydra's canonicalGameKeyV2 has arrived yet — only a
 * real doubleheader (gameNo>1, which only the v2 field carries) splits into a
 * second row, so the defensive fallback can't accidentally split a normal game.
 * Pass-through for sport-less (3-seg) or non-canonical strings (event tickers)
 * so those fallbacks survive untouched.
 */
export function promoteToGameKeyV2(legacyKey: string | null | undefined): string {
  if (!legacyKey) return '';
  const parsed = parseGameKeyV2(legacyKey);
  if (!parsed || !parsed.sport) return legacyKey;
  return `${parsed.sport}|${parsed.date}|${parsed.gameNo}|${parsed.teamLo}|${parsed.teamHi}`;
}

/**
 * Fold ONLY the leading sport segment of a canonical game key to g1's sport id,
 * leaving date, gameNo and teams byte-identical. Two keys that survive this and
 * compare equal are the same date and the same team pair within one sport
 * family — which is the same game.
 *
 * The same vocabulary gap `gameKeyMatchesUrl` already folds for, applied to
 * key-vs-key comparison: hydra stamps `ncaaf` in canonical keys while URLs and
 * g1 sport ids say `cfb`, so a raw `===` silently answers "no such game". An
 * unmapped spelling folds to itself (`foldKeySport` never widens a mismatch),
 * so a wire code g1 has never seen cannot collapse onto an unrelated key.
 *
 * Sport-less (3-seg) keys pass through: their first segment is a date, which
 * maps to nothing.
 */
export function foldGameKeySport(key: string): string {
  const cut = key.indexOf('|');
  if (cut <= 0) return key;
  return `${foldKeySport(key.slice(0, cut))}${key.slice(cut)}`;
}

/**
 * Strip a synthetic spread/total/multi/prop suffix off a GameRow key, returning
 * the base event key. SpreadsTotals synthesizes per-line rows as
 * `<eventKey>|spread|<ticker>` / `<eventKey>|total|<ticker>` / `<eventKey>|multi|<ticker>`
 * (see synthesizeFromSpread / synthesizeFromTotal), and the prop panels carry a
 * single `<eventKey>|prop-<stat>-<player>-<threshold>` segment; the base portion
 * IS the doubleheader-safe v2 event key. A plain moneyline key (no suffix) passes
 * through unchanged.
 *
 * This is the one boundary between a synthetic panel key and its underlying game
 * — canonical-key lookups (gameStates, consensus, open-order matching) must all
 * compare against THIS, not the raw synthetic key. Centralized here so the
 * ExpandedGamePanel lookup and GameOpenOrders order matching can't drift.
 *
 * #272 note: `|prop-` (with the hyphen) is the marker, NOT `|prop`. The trailing
 * delimiter is what keeps an unrelated `|property…` segment from being stripped
 * and collapsing a non-game key onto a real game — this function now gates a
 * DESTRUCTIVE per-game cancel, so an over-eager strip authorizes a sweep against
 * the wrong scope. Widen this list only with the same delimiter discipline.
 */
const SYNTHETIC_GAME_MARKERS = ['|spread|', '|total|', '|multi|', PROP_SYNTH_MARKER] as const;

export function baseGameKey(key: string | null | undefined): string {
  if (!key) return '';
  for (const marker of SYNTHETIC_GAME_MARKERS) {
    const i = key.indexOf(marker);
    if (i >= 0) return key.slice(0, i);
  }
  return key;
}

/**
 * Compose the v2 key from per-game-detail URL parts. Requires sport + date +
 * both teams. gameNo defaults to 1 (game-1 URLs omit the slot). Teams alpha-sort
 * to match hydra's convention (the URL renders away_home, the key sorts), so the
 * mapping is deterministic regardless of which side the URL put first. Returns
 * null on malformed input so the route can fall back / 404.
 */
export function composeGameKeyV2(parts: {
  sport: string | null | undefined;
  date: string | null | undefined;
  teamA: string | null | undefined;
  teamB: string | null | undefined;
  gameNo?: string | number | null | undefined;
}): string | null {
  const sport = parts.sport?.trim().toLowerCase();
  const date = parts.date?.trim();
  const a = parts.teamA?.trim().toUpperCase();
  const b = parts.teamB?.trim().toUpperCase();
  if (!sport || !date || !DATE_RE.test(date) || !a || !b) return null;
  let gameNo = 1;
  if (parts.gameNo != null && parts.gameNo !== '') {
    const n = Number(parts.gameNo);
    if (!Number.isInteger(n) || n < 1) return null;
    gameNo = n;
  }
  const [teamLo, teamHi] = [a, b].sort();
  return `${sport}|${date}|${gameNo}|${teamLo}|${teamHi}`;
}

/**
 * True when a stored row key (any 3/4/5-seg variant) refers to the game the
 * per-game-detail URL identifies. sport null/undefined on the URL side is a
 * wildcard (the legacy sport-less route); gameNo defaults to 1 on both sides.
 * Used by GameDetail's findGameRow/findGameEvent/findMultiOutcomeGame scan.
 */
export function gameKeyMatchesUrl(
  rowKey: string | null | undefined,
  url: {
    sport?: string | null;
    date?: string | null;
    teamA?: string | null;
    teamB?: string | null;
    gameNo?: string | number | null;
  },
): boolean {
  const row = parseGameKeyV2(rowKey);
  if (!row) return false;
  const date = url.date?.trim();
  const a = url.teamA?.trim().toUpperCase();
  const b = url.teamB?.trim().toUpperCase();
  if (!date || !a || !b) return false;
  const [lo, hi] = [a, b].sort();
  if (row.date !== date || row.teamLo !== lo || row.teamHi !== hi) return false;
  let gameNo = 1;
  if (url.gameNo != null && url.gameNo !== '') {
    const n = Number(url.gameNo);
    if (!Number.isInteger(n) || n < 1) return false;
    gameNo = n;
  }
  if (row.gameNo !== gameNo) return false;
  const urlSport = url.sport?.trim().toLowerCase();
  if (urlSport && row.sport && foldKeySport(row.sport) !== foldKeySport(urlSport)) return false;
  return true;
}

/**
 * Build the sport-aware per-game-detail route with a doubleheader gameNo slot:
 *   /game/{sport}/{date}/{AWAY}/{HOME}            (gameNo 1 — slot omitted)
 *   /game/{sport}/{date}/{AWAY}/{HOME}/{gameNo}   (gameNo > 1)
 * The v2 key alpha-sorts teams (BOS|NYY) but the URL renders game-day order
 * (away/home), so pass awayCode/homeCode from the row (canonicalAwayCode /
 * canonicalHomeCode on Kalshi). `suffix` appends a child segment (e.g. 'sources').
 */
export function gameDetailHref(args: {
  sport: string;
  date: string;
  awayCode: string;
  homeCode: string;
  gameNo?: number;
  suffix?: string;
}): string {
  const base = `/game/${encodeURIComponent(args.sport.toLowerCase())}/${args.date}/${encodeURIComponent(args.awayCode)}/${encodeURIComponent(args.homeCode)}`;
  const withNo = args.gameNo && args.gameNo > 1 ? `${base}/${args.gameNo}` : base;
  return args.suffix ? `${withNo}/${args.suffix}` : withNo;
}

/**
 * Whether a `/game/...` route can render anything useful for a sport.
 *
 * ATP/WTA cannot. GameDetail has no tennis handling at all, so every tennis
 * detail URL — sport-aware or legacy — resolves to a contentless shell. That
 * is not an inference: it is the premise of #248, recorded in
 * `docs/position-tracker-focused-dashboard-deep-links.md` ("Game Detail
 * renders a contentless shell for tennis; there is no working destination to
 * preserve"), and the reason that issue rerouted Position Tracker's graph
 * chips to a focused Value Dashboard link instead.
 *
 * Deliberately NARROWER than `focusedSportForChip`, which also routes CFB.
 * CFB's Game Detail page works fine — #539 rerouted it as a destination
 * PREFERENCE. Folding the two together would claim CFB has no detail page and
 * silently strip a working link, so the distinction is load-bearing and this
 * predicate answers only the "is the destination broken" half.
 *
 * Callers use it to decide whether to mint a detail affordance at all. It is
 * NOT wired into `gameDetailHrefForRow` below: that helper is shared with
 * Position Tracker and PropWorkbench, and changing it would move surfaces
 * this issue does not fence. Neither is currently exposed: Position Tracker
 * sends tennis to the focused dashboard before it ever reaches the legacy
 * href (#248's `focusedSportForChip`), and PropWorkbench renders the href as
 * an inert seam rather than a link. Those are two different reasons, not one
 * shared rule — a future caller gets neither for free.
 */
export function hasGameDetailSurface(sport: string): boolean {
  const s = sport.toLowerCase();
  return s !== 'atp' && s !== 'wta';
}

/**
 * Build the sport-aware per-game-detail route from a marketStore row (GameRow /
 * GameEventRow / MultiOutcomeEvent). Date + gameNo come from the row's v2 key
 * (so the URL date == the key's ET date the page matches against); away/home come
 * from the row's display codes. Returns null when the row key isn't a canonical
 * game key (e.g. an event-ticker fallback) so callers can fall back to a legacy
 * link or skip the link entirely.
 */
export function gameDetailHrefForRow(
  row: { key: string; sport: string; awayCode: string; homeCode: string },
  suffix?: string,
): string | null {
  const parts = parseGameKeyV2(row.key.split('|multi|')[0]);
  if (!parts) return null;
  return gameDetailHref({
    sport: row.sport,
    date: parts.date,
    awayCode: row.awayCode,
    homeCode: row.homeCode,
    gameNo: parts.gameNo,
    suffix,
  });
}

/** Parse a Hydra canonical key into its components. Returns null on any malformed input. */
export function parseHydraGameKey(key: string | null | undefined): HydraGameKeyParts | null {
  if (!key) return null;
  const parts = key.split('|');
  if (parts.length !== 3) return null;
  const [date, teamA, teamB] = parts as [string, string, string];
  if (!DATE_RE.test(date)) return null;
  if (!teamA || !teamB) return null;
  return { date, teamA, teamB };
}

// Compose a Hydra canonical key from three loose path-style segments
// (the GameDetail page uses `/game/:date/:teamA/:teamB`). We alpha-sort the
// teams to match hydra's canonical convention so URLs are stable regardless
// of which side a caller put first. Returns null on malformed input so the
// route can 404 instead of issuing garbage downstream.
export function composeHydraGameKey(parts: {
  date: string | null | undefined;
  teamA: string | null | undefined;
  teamB: string | null | undefined;
}): string | null {
  const date = parts.date?.trim();
  const a = parts.teamA?.trim().toUpperCase();
  const b = parts.teamB?.trim().toUpperCase();
  if (!date || !DATE_RE.test(date)) return null;
  if (!a || !b) return null;
  const [teamA, teamB] = [a, b].sort();
  return `${date}|${teamA}|${teamB}`;
}

/**
 * True when a G1 gameKey (any variant) refers to the same game as a Hydra
 * canonical key. Use when matching analytics rows back to local store entries.
 */
export function gameKeysMatch(g1Key: string | null | undefined, hydraKey: string | null | undefined): boolean {
  const normalized = toHydraGameKey(g1Key);
  if (!normalized || !hydraKey) return false;
  return normalized === hydraKey;
}
